import styled from 'styled-components';

import { formatEuroAmount } from 'helpers/price';

import { GetOrganizationInvoiceSimulationQuery, GetOrganizationQuery } from 'data/__generated__';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';

import { Amount, BlockTitle, Card, CardHeader, CardMeta, CardTitle, Muted } from './components/Card';
import { INVOICE_CATEGORY_LABELS, formatPeriod, formatVatRate } from './components/formatters';

type Simulation = GetOrganizationInvoiceSimulationQuery['getOrganizationInvoiceSimulation'];

// Les lignes de note (`line_note` côté Odoo) ne portent ni montant ni taxe : ce sont les lignes d'info de la
// facture (période, nom de machine, nombre de consommations). On les rend telles quelles, dans l'ordre du
// serveur, pour que la carte se lise comme le document Odoo.
const NOTE_LINE_TYPE = 'note';

export const InvoiceSimulationSection = ({
    simulation,
    loading,
    error,
    organization,
    creatingInvoice,
    handleCreateOdooInvoiceDraft,
}: {
    simulation?: Simulation;
    loading: boolean;
    error?: Error;
    organization: GetOrganizationQuery['organization'];
    creatingInvoice: boolean;
    handleCreateOdooInvoiceDraft: () => Promise<void>;
}) => {
    if (loading) {
        return (
            <Card>
                <Muted>Simulation en cours…</Muted>
            </Card>
        );
    }
    if (error) {
        return (
            <Card>
                <Muted>Impossible de calculer la simulation : {error.message}</Muted>
            </Card>
        );
    }
    if (!simulation) {
        return null;
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle>Facture potentielle (simulation)</CardTitle>
                <CardMeta>
                    {formatPeriod(simulation.startDate, simulation.endDate)} · Encaissement{' '}
                    {simulation.collector === 'stripe' ? 'Stripe (CB/SEPA)' : 'Odoo'}
                </CardMeta>
            </CardHeader>

            {simulation.groups.length === 0 ? (
                <Muted>Rien à facturer sur la période.</Muted>
            ) : (
                simulation.groups.map((group) => (
                    <Group key={group.category}>
                        <BlockTitle>{INVOICE_CATEGORY_LABELS[group.category] ?? group.category}</BlockTitle>
                        <Lines>
                            {group.lines.map((line, index) =>
                                line.type === NOTE_LINE_TYPE ? (
                                    <InfoLine key={`${group.category}-${index}`}>{line.name}</InfoLine>
                                ) : (
                                    <Line key={`${group.category}-${index}`}>
                                        <LineName>
                                            {line.name}
                                            {line.quantity != null &&
                                                ` (${line.quantity} × ${formatEuroAmount(line.priceUnit ?? 0)})`}
                                        </LineName>
                                        <LineVat>{formatVatRate(line.taxRate)}</LineVat>
                                        <Amount>
                                            {formatEuroAmount((line.quantity ?? 1) * (line.priceUnit ?? 0))}
                                        </Amount>
                                    </Line>
                                ),
                            )}
                        </Lines>
                    </Group>
                ))
            )}

            <Totals>
                <TotalsRow>
                    <span>Montant HT</span>
                    <Amount>{formatEuroAmount(simulation.totalHT)}</Amount>
                </TotalsRow>
                {[...simulation.totalsByVat]
                    .sort((vatA, vatB) => vatA.taxRate - vatB.taxRate)
                    .map(({ taxRate, amountHT, amountVat }) => (
                        <TotalsRow key={taxRate} $isMuted>
                            <span>
                                {formatVatRate(taxRate)} (base {formatEuroAmount(amountHT)})
                            </span>
                            <Amount>{formatEuroAmount(amountVat)}</Amount>
                        </TotalsRow>
                    ))}
                <TotalsRow $isTotal>
                    <span>Total TTC</span>
                    <Amount>{formatEuroAmount(simulation.totalTTC)}</Amount>
                </TotalsRow>
            </Totals>

            {organization.odooOrganizationId && (
                <OdooInvoiceButtonContainer>
                    <TotemPrimaryButton type="button" onClick={handleCreateOdooInvoiceDraft} disabled={creatingInvoice}>
                        {creatingInvoice ? 'Création en cours...' : 'Créer brouillon Odoo'}
                    </TotemPrimaryButton>
                </OdooInvoiceButtonContainer>
            )}
        </Card>
    );
};

const Group = styled.div`
    margin-bottom: 12px;
`;

// Une seule grille par groupe : les colonnes des lignes produit restent alignées, les lignes d'info
// occupent toute la largeur.
const Lines = styled.div`
    display: grid;
    grid-template-columns: 1fr auto auto;
    column-gap: 12px;
`;

const Line = styled.div`
    display: contents;
`;

const InfoLine = styled.div`
    grid-column: 1 / -1;
    padding: 4px 0 2px;
    color: ${({ theme }) => theme.ctaPrimaryTextColor};
    font-size: 13px;
    font-style: italic;
    white-space: pre-line;
`;

const LineName = styled.span`
    padding: 2px 0;
    color: ${({ theme }) => theme.textColor};
    white-space: pre-line;
`;

const LineVat = styled.span`
    padding: 2px 0;
    color: ${({ theme }) => theme.ctaPrimaryTextColor};
    font-size: 13px;
`;

// Pied de facture Odoo : montant HT, puis une ligne par groupe de taxe, puis le total TTC.
const Totals = styled.div`
    border-top: 1px solid ${({ theme }) => theme.lightBorderColor};
    margin-top: 8px;
    padding-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const TotalsRow = styled.div<{ $isMuted?: boolean; $isTotal?: boolean }>`
    display: flex;
    justify-content: space-between;
    color: ${({ theme, $isMuted }) => ($isMuted ? theme.ctaPrimaryTextColor : theme.textColor)};
    font-size: ${({ $isMuted }) => ($isMuted ? '13px' : 'inherit')};
    font-weight: ${({ $isTotal }) => ($isTotal ? 700 : 400)};
    ${({ theme, $isTotal }) =>
        $isTotal ? `border-top: 1px solid ${theme.lightBorderColor}; margin-top: 6px; padding-top: 6px;` : ''}
`;

const OdooInvoiceButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid ${({ theme }) => theme.lightBorderColor};
`;
